import { useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom"; // 1. Importamos useLocation
import {
	Menu,
	Home,
	Users,
	ClipboardList,
	Settings,
	Headset,
	ChevronDown,
	Briefcase,
	Tags,
	Wallet,
	Star,
	FileArchive
} from "lucide-react";
import { useAuthSessionContext } from "../../routing/auth-session-store";

interface Props {
	collapsedByDefault?: boolean;
	className?: string;
}

const sidebarItems = [
	{ label: "Panel Principal", to: "/admin/dashboard", Icon: Home },
	{ label: "Clientes", to: "/admin/clientes", Icon: Users },
	{ label: "Pedidos", to: "/admin/pedidos", Icon: ClipboardList },
	{
		label: "Soporte",
		Icon: Headset,
		subItems: [
			{ label: "Arbitraje", to: "/admin/arbitraje" },
			{ label: "Personal de Soporte", to: "/admin/personal-administrativo" },
		],
	},
	{ label: "Proveedores", to: "/admin/proveedor", Icon: Briefcase },
	{ label: "Conciliación de Pagos", to: "/admin/conciliacion-pagos", Icon: Wallet },
	{ label: "Reseñas", to: "/admin/resenas", Icon: Star },
	{ label: "Cupones", to: "/admin/cupones", Icon: Tags },
	{ label: "Configuraciones", to: "/admin/configuracion", Icon: Settings },
	{ label: "Manual de Usuario", to: "/admin/manual", Icon: FileArchive },
];

export function SidebarManagment({ collapsedByDefault = true, className = "" }: Props) {
	const [collapsed, setCollapsed] = useState(collapsedByDefault);
	const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
	const { session } = useAuthSessionContext();

	// 2. Obtenemos la ubicación actual
	const location = useLocation();

	const toggleSubMenu = (label: string) => {
		setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }));
	};

	const visibleSidebarItems = useMemo(() => {
		const userType = session?.user?.type_user;

		if (userType !== "Soporte") {
			return sidebarItems;
		}

		const allowedTopLevel = new Set([
			"Panel Principal",
			"Pedidos",
			"Soporte",
			"Proveedores",
			"Conciliación de Pagos",
		]);

		const allowedSupportSubItems = new Set(["Arbitraje"]);

		return sidebarItems
			.filter((item) => allowedTopLevel.has(item.label))
			.map((item) => {
				if (!item.subItems) return item;

				return {
					...item,
					subItems: item.subItems.filter((subItem) =>
						allowedSupportSubItems.has(subItem.label),
					),
				};
			});
	}, [session?.user?.type_user]);

	return (
		<aside
			className={`${
				collapsed ? "w-16 p-2" : "w-64 px-4 py-2"
			} border-r border-slate-300 bg-[#fcfcfc] transition-[width,padding] duration-300 ease-in-out z-40 relative flex h-auto min-h-[calc(100vh-160px)] self-stretch flex-col ${className}`}
		>
			<nav className='flex flex-col gap-4 text-slate-900 w-full'>
				<div
					className={`flex items-center ${collapsed ? "justify-center" : "justify-end"}`}
				>
					<button
						type='button'
						aria-expanded={!collapsed}
						onClick={() => setCollapsed((prev) => !prev)}
						aria-label={collapsed ? "Abrir menú" : "Cerrar menú"}
						className='flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 shadow-sm transition-colors hover:bg-slate-100 focus:outline-none cursor-pointer'
					>
						<Menu className='h-4 w-4' />
					</button>
				</div>

				<ul className='flex flex-col gap-2 text-sm w-full'>
					{visibleSidebarItems.map(({ label, Icon, to, subItems }) => {
						const isMenuOpen = openMenus[label];

						// 3. Lógica para saber si el padre está activo (si alguna ruta hija coincide)
						const isParentActive = subItems
							? subItems.some((sub) => location.pathname.startsWith(sub.to))
							: false;

						return (
							<li
								key={label}
								className='relative group flex flex-col justify-center w-full'
							>
								{subItems ? (
									<button
										onClick={() => toggleSubMenu(label)}
										// 4. Aplicamos estilos condicionales basados en isParentActive
										className={`flex items-center transition-all duration-300 font-medium rounded-full cursor-pointer ${
											collapsed
												? "h-10 w-10 justify-center p-0 mx-auto"
												: "h-10 w-full justify-between px-4 gap-3"
										} ${
											isParentActive
												? "bg-primary text-white shadow-sm" // Estilo Activo
												: "text-slate-800 hover:bg-slate-100" // Estilo Inactivo
										}`}
									>
										<div
											className={`flex items-center ${collapsed ? "" : "gap-3"}`}
										>
											{/* 5. Cambiamos el color del icono si está activo */}
											<Icon
												className={`h-5 w-5 shrink-0 transition-colors ${
													isParentActive
														? "text-white"
														: "text-slate-500 group-hover:text-primary"
												}`}
											/>
											<span
												className={`min-w-0 whitespace-nowrap text-left transition-all duration-300 ease-in-out ${
													collapsed
														? "opacity-0 max-w-0 w-0 overflow-hidden delay-0"
														: "opacity-100 max-w-full w-auto delay-75"
												}`}
											>
												{label}
											</span>
										</div>

										{!collapsed && (
											<ChevronDown
												className={`h-4 w-4 transition-transform duration-300 ${
													isMenuOpen ? "rotate-180" : ""
												} ${
													isParentActive
														? "text-white"
														: "text-slate-500"
												}`}
											/>
										)}
									</button>
								) : (
									<NavLink
										to={to as string}
										aria-label={label}
										className={({ isActive }) =>
											`flex items-center transition-all duration-300 font-medium rounded-full ${
												collapsed
													? "h-10 w-10 justify-center p-0 mx-auto"
													: "h-10 w-full justify-start px-4 gap-3"
											} ${
												isActive
													? "bg-primary text-white shadow-sm"
													: "text-slate-800 hover:bg-slate-100"
											}`
										}
									>
										{({ isActive }) => (
											<>
												<Icon
													className={`h-5 w-5 shrink-0 transition-colors ${
														isActive
															? "text-white"
															: "text-slate-500"
													}`}
												/>
												<span
													className={`min-w-0 whitespace-nowrap text-left transition-all duration-300 ease-in-out ${
														collapsed
															? "opacity-0 max-w-0 w-0 overflow-hidden delay-0"
															: "opacity-100 max-w-full w-auto delay-75"
													}`}
												>
													{label}
												</span>
											</>
										)}
									</NavLink>
								)}

								{subItems && (
									<>
										{!collapsed && isMenuOpen && (
											<ul className='flex flex-col gap-1 mt-1 pl-12 pr-2 animate-in slide-in-from-top-2 fade-in duration-200'>
												{subItems.map((subItem) => (
													<li key={subItem.label}>
														<NavLink
															to={subItem.to}
															className={({ isActive }) =>
																`block py-2 px-3 rounded-lg text-sm transition-colors ${
																	isActive
																		? "text-primary font-semibold bg-primary/10"
																		: "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
																}`
															}
														>
															{subItem.label}
														</NavLink>
													</li>
												))}
											</ul>
										)}

										{collapsed && (
											<div className='absolute left-full top-0 hidden group-hover:block z-50 pl-3'>
												<div className='bg-white border border-slate-200 shadow-lg rounded-xl py-2 min-w-50 animate-in fade-in zoom-in-95 duration-200'>
													<div className='px-4 py-1 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1'>
														{label}
													</div>
													{subItems.map((subItem) => (
														<NavLink
															key={subItem.label}
															to={subItem.to}
															className={({ isActive }) =>
																`block py-2 px-4 text-sm transition-colors ${
																	isActive
																		? "text-primary font-semibold bg-primary/5 border-l-2 border-primary"
																		: "text-slate-700 hover:bg-slate-50 hover:text-primary border-l-2 border-transparent"
																}`
															}
														>
															{subItem.label}
														</NavLink>
													))}
												</div>
											</div>
										)}
									</>
								)}
							</li>
						);
					})}
				</ul>
			</nav>
		</aside>
	);
}

export default SidebarManagment;

